/**
 * contentPlanFlowReadiness — pure count-gathering for the M2 wizard.
 *
 * D12 rule (Content Plan Flow Polish): the wizard NEVER implements step
 * predicates locally. This module only converts raw Firestore documents
 * (delivered by live subscriptions) into `ContentPlanReadinessCounts`, then
 * everything flows through THE single predicate implementation
 * `evaluateContentPlanFlowReadiness` from `@modules/shared-types` — the same
 * function `content-plan doctor` runs server-side. Parity is by construction
 * and locked by `__tests__/contentPlanFlowReadiness.test.ts`.
 */

import {
  evaluateContentPlanFlowReadiness,
  type CliContentPlanFlowReadinessData,
  type ContentPlanReadinessCounts,
} from '@modules/shared-types';

// ─── Minimal doc shapes the gatherer needs (structural subsets) ──────

export interface ReadinessAuthorDoc {
  id?: string;
  isDefault?: boolean;
}

export interface ReadinessPillarDoc {
  id: string;
  isDraft?: boolean;
  authorIds?: string[];
}

export interface ReadinessTopicDoc {
  status?: string;
}

export interface ReadinessStrategyDoc {
  id?: string;
  createdAt?: unknown;
}

export interface ContentPlanReadinessSources {
  authors: ReadinessAuthorDoc[];
  /** Platforms of the effective author (already scoped by the subscription). */
  platformCount: number;
  /** ALL project pillars — author scoping happens here (mirrors TopicPillarsSection). */
  pillars: ReadinessPillarDoc[];
  /** Topics of the effective author (already scoped by the subscription). */
  topics: ReadinessTopicDoc[];
  /** Strategies of the effective author (already scoped by the subscription). */
  strategies: ReadinessStrategyDoc[];
  /** Publications of the NEWEST strategy (0 when no strategy). */
  newestStrategyPublicationCount: number;
  /** Canonical posts of the effective author. */
  postCount: number;
  /** Workspace-selected author (URL `?author=`); null in overview mode. */
  activeAuthorId?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function createdAtMillis(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (
    typeof value === 'object'
    && value !== null
    && 'toMillis' in value
    && typeof (value as { toMillis: () => number }).toMillis === 'function'
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }
  return 0;
}

/**
 * The author whose workspace the wizard observes: the URL-selected author
 * when valid, else the default author, else the sole author.
 */
export function resolveReadinessAuthorId(
  authors: ReadinessAuthorDoc[],
  activeAuthorId?: string | null,
): string | null {
  if (activeAuthorId && authors.some((author) => author.id === activeAuthorId)) {
    return activeAuthorId;
  }
  const defaultAuthor = authors.find((author) => author.isDefault);
  if (defaultAuthor?.id) return defaultAuthor.id;
  if (authors.length === 1 && authors[0].id) return authors[0].id;
  return null;
}

/** Newest strategy by createdAt (publications predicate targets it). */
export function pickNewestStrategyId(strategies: ReadinessStrategyDoc[]): string | null {
  let best: ReadinessStrategyDoc | null = null;
  let bestMillis = -1;
  for (const strategy of strategies) {
    const millis = createdAtMillis(strategy.createdAt);
    if (millis >= bestMillis) {
      best = strategy;
      bestMillis = millis;
    }
  }
  return best?.id ?? null;
}

/**
 * Convert raw subscription data into the D12 counts contract. NO predicate
 * logic here — thresholds/comparisons live ONLY in the shared evaluator.
 */
export function gatherContentPlanReadinessCounts(
  sources: ContentPlanReadinessSources,
): ContentPlanReadinessCounts {
  const effectiveAuthorId = resolveReadinessAuthorId(sources.authors, sources.activeAuthorId);

  // Pillar author scoping mirrors TopicPillarsSection: with an author, only
  // pillars whose authorIds[] contains it; without one, all pillars.
  const scopedPillars = effectiveAuthorId
    ? sources.pillars.filter(
        (pillar) => Array.isArray(pillar.authorIds) && pillar.authorIds.includes(effectiveAuthorId),
      )
    : sources.pillars;

  return {
    authorCount: sources.authors.length,
    // D12: "a default (or sole) author document exists."
    authorResolved: sources.authors.length === 1 || sources.authors.some((a) => Boolean(a.isDefault)),
    platformCount: sources.platformCount,
    pillarCount: scopedPillars.length,
    confirmedPillarCount: scopedPillars.filter((pillar) => pillar.isDraft !== true).length,
    readyTopicCount: sources.topics.filter((topic) => topic.status === 'ready').length,
    strategyCount: sources.strategies.length,
    publicationCount: sources.strategies.length > 0 ? sources.newestStrategyPublicationCount : 0,
    postCount: sources.postCount,
  };
}

/** Convenience: gather + evaluate in one step (what the hook returns). */
export function computeContentPlanFlowReadiness(
  sources: ContentPlanReadinessSources,
): Pick<CliContentPlanFlowReadinessData, 'ready' | 'steps'> {
  return evaluateContentPlanFlowReadiness(gatherContentPlanReadinessCounts(sources));
}
